<template>
  <div ref="root" class="relative">
    <input
      class="w-full p-2 rounded-md border border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-600 placeholder-gray-400"
      v-model="q"
      :placeholder="placeholder"
      @focus="onFocus"
      @input="onInput"
      @keydown="onKeydown"
      @paste="onPaste"
      autocomplete="off"
    />
    <div v-if="modelValue && modelValue.length" class="mt-2 flex flex-wrap gap-2">
      <span
        v-for="em in modelValue"
        :key="em"
        class="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-gray-100 text-gray-800 border border-gray-300
               dark:bg-gray-400 dark:border-gray-600 dark:text-gray-100 text-xs"
      >
        {{ em }}
        <button
          type="button"
          @click="removeEmail(em)"
          title="Remove"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-red-500">
            <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clip-rule="evenodd" />
          </svg>
        </button>
      </span>
    </div>

    <div
      v-if="open"
      ref="dropdown"
      class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-64 overflow-auto"
    >
      <div
        v-for="(item, idx) in filtered"
        :key="item.id + ':' + item.email"
        :ref="(el) => setItemRef(el, idx)"
        class="px-3 py-2 cursor-pointer text-sm"
        :class="idx === activeIndex ? 'bg-gray-100 dark:bg-gray-700' : ''"
        @mouseenter="activeIndex = idx"
        @mousedown.prevent="choose(item)"
      >
        <div class="font-medium text-gray-900 dark:text-gray-100">
          {{ item.name || item.email }}
        </div>
        <div class="text-xs text-gray-600 dark:text-gray-300">
          {{ item.email }} <span v-if="item.company">• {{ item.company }}</span>
        </div>
      </div>

      <div class="px-3 py-2 text-xs text-gray-500 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">
        Tip: Enter/comma adds typed email (even if not in address book).
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EmailTypeahead",
  props: {
    modelValue: { type: Array, default: () => [] },
    addressBook: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Type and press Enter" }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      q: "",
      open: false,
      activeIndex: -1,
      itemEls: []
    };
  },
  computed: {
    selectedSet() {
      return new Set((this.modelValue || []).map(e => String(e).toLowerCase()));
    },
    filtered() {
      const q = this.q.trim().toLowerCase();
      if (!q) return [];

      return this.addressBook
        .map(c => ({
          id: c.id,
          name: (c.name || `${c.name_first || ""} ${c.name_last || ""}`).trim(),
          email: (c.email || "").trim(),
          company: c.company?.companyname || c.companyname || ""
        }))
        .filter(x => x.email)
        .filter(x => (`${x.name} ${x.email} ${x.company}`.toLowerCase().includes(q)))
        .filter(x => !this.selectedSet.has(x.email.toLowerCase()));
    }
  },
  watch: {
    filtered() {
      this.itemEls = [];
      if (this.open) {
        this.$nextTick(() => this.scrollActiveIntoView());
      }
    }
  },
  mounted() {
    this._onDocClick = (e) => {
      const root = this.$refs.root;
      if (root && !root.contains(e.target)) {
        this.open = false;
        this.activeIndex = -1;
      }
    };
    document.addEventListener("mousedown", this._onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this._onDocClick);
  },
  methods: {
    setItemRef(el, idx) {
      if (el) this.itemEls[idx] = el;
    },
    scrollActiveIntoView() {
      const dd = this.$refs.dropdown;
      const el = this.itemEls[this.activeIndex];
      if (!dd || !el) return;
      const ddRect = dd.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      const padding = 8;

      if (elRect.top < ddRect.top + padding) {
        dd.scrollTop -= (ddRect.top + padding) - elRect.top;
      } else if (elRect.bottom > ddRect.bottom - padding) {
        dd.scrollTop += elRect.bottom - (ddRect.bottom - padding);
      }
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
    },
    addEmail(raw) {
      const email = String(raw || "").trim();
      if (!email) return;

      const parts = email.split(",").map(s => s.trim()).filter(Boolean);
      const next = [...(this.modelValue || [])];

      for (const p of parts) {
        if (!this.isValidEmail(p)) continue;
        if (next.some(x => String(x).toLowerCase() === p.toLowerCase())) continue;
        next.push(p);
      }

      this.$emit("update:modelValue", next);
      this.q = "";
      this.open = false;
      this.activeIndex = -1;
    },
    removeEmail(email) {
      this.$emit(
        "update:modelValue",
        (this.modelValue || []).filter(e => String(e).toLowerCase() !== String(email).toLowerCase())
      );
    },
    choose(item) {
      this.addEmail(item.email);
    },
    onFocus() {
      if (this.filtered.length) {
        this.open = true;
        this.activeIndex = 0;
        this.$nextTick(() => this.scrollActiveIntoView());
      }
    },
    onInput() {
      this.open = !!this.q.trim() && this.filtered.length > 0;
      this.activeIndex = this.open ? 0 : -1;
      this.$nextTick(() => this.scrollActiveIntoView());
    },
    onPaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData("text");
      if (text && text.includes(",")) {
        e.preventDefault();
        this.addEmail(text);
      }
    },
    onKeydown(e) {
      if (e.key === "Escape") {
        this.open = false;
        this.activeIndex = -1;
        return;
      }
      if (e.key === ",") {
        e.preventDefault();
        this.addEmail(this.q);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (this.open && this.filtered.length && this.activeIndex >= 0) {
          this.choose(this.filtered[this.activeIndex]);
        } else {
          this.addEmail(this.q);
        }
        return;
      }

      if (!this.open) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.activeIndex = Math.min(this.filtered.length - 1, this.activeIndex + 1);
        this.$nextTick(() => this.scrollActiveIntoView());
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.activeIndex = Math.max(0, this.activeIndex - 1);
        this.$nextTick(() => this.scrollActiveIntoView());
      }
    }
  }
};
</script>
